import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  TextField,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import assessmentService from '../services/assessmentService';
import { toast } from 'react-toastify';

const CreateAssessmentPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openPreview, setOpenPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const [assessment, setAssessment] = useState({
    title: '',
    description: '',
    category: '',
    estimatedTime: '',
    instructions: '',
    questions: [
      {
        id: 1,
        text: '',
        type: 'multiple-choice',
        options: ['', '', '', ''],
        required: true
      }
    ]
  });

  const handleLogout = () => {
    logout();
  };

  const goBack = useSafeBack();

  const questionTypes = [
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'scale', label: 'Rating Scale (1-5)' },
    { value: 'text', label: 'Text Response' },
    { value: 'yes-no', label: 'Yes/No' }
  ];

  const categories = [
    'Anxiety Assessment',
    'Depression Screening',
    'Stress Evaluation',
    'General Mental Health',
    'Substance Use',
    'Academic Stress',
    'Social Anxiety',
    'Sleep Quality'
  ];

  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      text: '',
      type: 'multiple-choice',
      options: ['', '', '', ''],
      required: true
    };
    setAssessment(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const removeQuestion = (questionId: number) => {
    setAssessment(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const updateQuestion = (questionId: number, field: string, value: any) => {
    setAssessment(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, [field]: value } : q
      )
    }));
  };

  const updateQuestionOption = (questionId: number, optionIndex: number, value: string) => {
    setAssessment(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { 
              ...q, 
              options: q.options.map((opt, idx) => idx === optionIndex ? value : opt)
            } 
          : q
      )
    }));
  };

  const validateAssessment = () => {
    if (!assessment.title.trim()) {
      toast.error('Assessment title is required');
      return false;
    }
    
    if (!assessment.category) {
      toast.error('Assessment category is required');
      return false;
    }

    if (assessment.questions.length === 0) {
      toast.error('At least one question is required');
      return false;
    }

    // Validate each question
    for (let i = 0; i < assessment.questions.length; i++) {
      const question = assessment.questions[i];
      if (!question.text.trim()) {
        toast.error(`Question ${i + 1} text is required`);
        return false;
      }

      if (question.type === 'multiple-choice') {
        const validOptions = question.options.filter(opt => opt.trim());
        if (validOptions.length < 2) {
          toast.error(`Question ${i + 1} must have at least 2 answer options`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateAssessment()) {
      return;
    }

    try {
      setSaving(true);

      // Format questions according to backend expectations
      const formattedQuestions = assessment.questions.map((q, index) => {
        // Map frontend question type to backend format (UPPERCASE)
        let questionType = q.type.toUpperCase().replace('-', '_');
        if (questionType === 'MULTIPLE_CHOICE') {
          questionType = 'MULTIPLE_CHOICE';
        } else if (questionType === 'YES_NO') {
          questionType = 'YES_NO';
        } else if (questionType === 'SCALE') {
          questionType = 'SCALE';
        } else if (questionType === 'TEXT') {
          questionType = 'TEXT';
        }

        // Format options according to backend expectations
        const formattedOptions = [];
        if (q.type === 'multiple-choice' && q.options) {
          for (let i = 0; i < q.options.length; i++) {
            const option = q.options[i];
            if (option.trim()) {
              formattedOptions.push({
                optionText: option.trim(),
                orderNumber: i + 1,
                points: 0,
                isRedFlag: false
              });
            }
          }
        }

        return {
          questionText: q.text.trim(),
          questionType: questionType,
          orderNumber: index + 1,
          isRequired: q.required,
          points: 0,
          isRedFlag: false,
          options: formattedOptions
        };
      });

      // Prepare assessment data for API
      const assessmentData = {
        title: assessment.title.trim(),
        description: assessment.description.trim(),
        category: assessment.category,
        instructions: assessment.instructions.trim(),
        estimatedMinutes: parseInt(assessment.estimatedTime) || 10,
        isActive: true,
        redFlagThreshold: 80,
        yellowFlagThreshold: 60,
        greenFlagThreshold: 40,
        questions: formattedQuestions
      };

      // Create the assessment first
      const createdAssessment = await assessmentService.createAssessment(assessmentData);
      
      toast.success('Assessment created successfully!');
      
      // Navigate back to assessments list or to edit the newly created assessment
      navigate(`/surveys/edit/${createdAssessment.id}?new=true`);

    } catch (error: any) {
      console.error('Error saving assessment:', error);
      const message = error.response?.data?.message || error.message || 'Failed to save assessment';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - Create Assessment
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" onClick={goBack}>
              ← Back to Dashboard
            </Button>
            <Typography variant="h4">
              Create New Assessment
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => setOpenPreview(true)} disabled={saving}>
              Preview
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Assessment'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Assessment Details */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assessment Details
                </Typography>
                
                <TextField
                  fullWidth
                  label="Assessment Title"
                  value={assessment.title}
                  onChange={(e) => setAssessment(prev => ({ ...prev, title: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={assessment.category}
                  onChange={(e) => setAssessment(prev => ({ ...prev, category: e.target.value }))}
                  sx={{ mb: 2 }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </TextField>
                
                <TextField
                  fullWidth
                  label="Estimated Time (minutes)"
                  type="number"
                  value={assessment.estimatedTime}
                  onChange={(e) => setAssessment(prev => ({ ...prev, estimatedTime: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={assessment.description}
                  onChange={(e) => setAssessment(prev => ({ ...prev, description: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Instructions for Students"
                  value={assessment.instructions}
                  onChange={(e) => setAssessment(prev => ({ ...prev, instructions: e.target.value }))}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Questions */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Questions ({assessment.questions.length})
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={addQuestion}
                  >
                    Add Question
                  </Button>
                </Box>

                {assessment.questions.map((question, index) => (
                  <Card key={question.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle1">
                          Question {index + 1}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={question.required ? 'Required' : 'Optional'} 
                            size="small" 
                            color={question.required ? 'primary' : 'default'}
                          />
                          {assessment.questions.length > 1 && (
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => removeQuestion(question.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      <TextField
                        fullWidth
                        label="Question Text"
                        value={question.text}
                        onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        select
                        label="Question Type"
                        value={question.type}
                        onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                        sx={{ mb: 2, minWidth: 200 }}
                      >
                        {questionTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      {(question.type === 'multiple-choice') && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Answer Options:
                          </Typography>
                          {question.options.map((option, optionIndex) => (
                            <TextField
                              key={optionIndex}
                              fullWidth
                              size="small"
                              label={`Option ${optionIndex + 1}`}
                              value={option}
                              onChange={(e) => updateQuestionOption(question.id, optionIndex, e.target.value)}
                              sx={{ mb: 1 }}
                            />
                          ))}
                        </Box>
                      )}

                      {question.type === 'scale' && (
                        <Typography variant="body2" color="textSecondary">
                          Students will rate from 1 (lowest) to 5 (highest)
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Preview Dialog */}
        <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
          <DialogTitle>Assessment Preview</DialogTitle>
          <DialogContent>
            <Typography variant="h5" gutterBottom>
              {assessment.title || 'Untitled Assessment'}
            </Typography>
            
            <Typography variant="body1" paragraph>
              {assessment.description}
            </Typography>
            
            <Typography variant="body2" color="textSecondary" paragraph>
              <strong>Category:</strong> {assessment.category || 'Not specified'}<br/>
              <strong>Estimated Time:</strong> {assessment.estimatedTime || 'Not specified'} minutes<br/>
              <strong>Questions:</strong> {assessment.questions.length}
            </Typography>
            
            {assessment.instructions && (
              <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Instructions:</Typography>
                <Typography variant="body2">{assessment.instructions}</Typography>
              </Box>
            )}

            {assessment.questions.map((question, index) => (
              <Box key={question.id} sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {index + 1}. {question.text || `Question ${index + 1}`}
                  {question.required && <Chip label="Required" size="small" sx={{ ml: 1 }} />}
                </Typography>
                
                {question.type === 'multiple-choice' && (
                  <Box sx={{ pl: 2 }}>
                    {question.options.map((option, optionIndex) => (
                      option && (
                        <Typography key={optionIndex} variant="body2" sx={{ mb: 0.5 }}>
                          ○ {option}
                        </Typography>
                      )
                    ))}
                  </Box>
                )}
                
                {question.type === 'scale' && (
                  <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>
                    Scale: 1 (Strongly Disagree) to 5 (Strongly Agree)
                  </Typography>
                )}
                
                {question.type === 'text' && (
                  <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>
                    [Text response field]
                  </Typography>
                )}
                
                {question.type === 'yes-no' && (
                  <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>
                    ○ Yes ○ No
                  </Typography>
                )}
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPreview(false)}>
              Close Preview
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default CreateAssessmentPage;